import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const SetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  const token = searchParams.get('token');
  
  useEffect(() => {
    if (!token) {
      setError('Link non valido. Contatta l\'amministratore per ricevere un nuovo link.');
    }
  }, [token]);
  
  // Validazione password
  const passwordChecks = {
    length: password.length >= 6,
    match: password === confirmPassword && password.length > 0
  };
  
  const isValid = passwordChecks.length && passwordChecks.match;
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isValid) {
      toast.error('Verifica i requisiti della password');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post('/api/auth/set-password', { 
        token, 
        password 
      }, {
        headers: { 'Content-Type': 'application/json' },
        withCredentials: true
      });
      
      const data = response.data;
      
      setSuccess(true);
      toast.success('Password impostata con successo!');
      
      // Login automatico - il cookie è già stato impostato dalla response del server
      if (data.access_token && data.user) {
        // Redirect dopo 2 secondi
        setTimeout(() => {
          window.location.href = '/';
        }, 2000);
      }
      
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Errore nell\'impostazione della password';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };
  
  // Schermata di successo
  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Password Impostata!</h2>
            <p className="text-gray-600 mb-4">
              La tua password è stata impostata con successo.<br/>
              Stai per essere reindirizzato...
            </p>
            <div className="animate-pulse">
              <div className="h-1 w-32 bg-green-200 rounded mx-auto"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Schermata di errore (token mancante o invalido)
  if (error && !token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Link Non Valido</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} className="bg-[#FF7819] hover:bg-[#e66a0f]">
              Vai al Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 to-amber-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center pb-2">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img
              src="/images/abitalo-logo.png" 
              alt="ABITALO"
              className="h-12"
            />
          </div>
          <CardTitle className="text-2xl">Imposta la tua Password</CardTitle>
          <CardDescription>
            Crea una password sicura per accedere al sistema
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password">Nuova Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Inserisci password"
                  className="pl-10 pr-10"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            
            {/* Conferma Password */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Conferma Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Conferma password"
                  className="pl-10"
                  autoComplete="new-password"
                />
              </div>
            </div>
            
            {/* Requisiti Password */}
            <div className="bg-gray-50 rounded-lg p-3 space-y-2">
              <p className="text-sm font-medium text-gray-700">Requisiti password:</p>
              <div className="space-y-1">
                <div className={`flex items-center gap-2 text-sm ${passwordChecks.length ? 'text-green-600' : 'text-gray-500'}`}>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordChecks.length ? 'bg-green-100' : 'bg-gray-200'}`}>
                    {passwordChecks.length && <CheckCircle className="w-3 h-3" />}
                  </div>
                  Almeno 6 caratteri
                </div>
                <div className={`flex items-center gap-2 text-sm ${passwordChecks.match ? 'text-green-600' : 'text-gray-500'}`}>
                  <div className={`w-4 h-4 rounded-full flex items-center justify-center ${passwordChecks.match ? 'bg-green-100' : 'bg-gray-200'}`}>
                    {passwordChecks.match && <CheckCircle className="w-3 h-3" />}
                  </div>
                  Le password coincidono
                </div>
              </div>
            </div>
            
            {/* Errore */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
            
            {/* Submit */}
            <Button 
              type="submit" 
              className="w-full bg-[#FF7819] hover:bg-[#e66a0f] text-white"
              disabled={!isValid || loading}
            >
              {loading ? 'Impostazione in corso...' : 'Imposta Password'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SetPasswordPage;
